import axios, { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';
import { RegisterTechnicalGradeInput, StudentRankingMode, StudentRankingResponse, StudentRankingScope } from '@/types/grade';
import { ApiResponse } from '@/types';
import {
  CreateStaffUserForm,
  StaffListFilters,
  UpdateStaffUserForm,
  User,
} from '@/types';
import {
  Student,
  StudentDetail,
  StudentStats,
  StudentsListResponse,
  AuthorizedPickupApiResponse,
  DocumentStatusApiResponse,
  StudentFilters,
  CreateStudentForm,
  StudentProfile,
  EnrollStudentForm,
  PromoteStudentForm,
  AuthorizedPickupForm,
  MarkDocumentReceivedForm,
  UpdateDocumentStatusForm,
} from '@/types/student';
import {
  Teacher,
  CreateTeacherForm,
  UpdateTeacherForm,
  TeachingAssignment,
  CreateTeachingAssignmentForm,
  CreateTeachingAssignmentsBulkForm,
  TeacherFilters,
} from '@/types/teacher';
import { InstitutionalActivity, InstitutionalActivityInput } from '@/types/institutionalActivity';

/**
 * Configuración de la API cliente con Axios
 * Maneja interceptores para autenticación, errores y respuestas
 */
class ApiClient {
  private client: AxiosInstance;

  constructor() {
    // Crear instancia de Axios con configuración base
    // En producción usar VITE_API_BASE_URL (ej. https://tu-api.com/api); en dev usa proxy '/api'
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Interceptor para agregar token de autenticación
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Si es FormData, dejar que el navegador establezca el Content-Type con el boundary correcto
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Interceptor para manejar respuestas
    this.client.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      (error: AxiosError<ApiResponse>) => {
        // Manejar errores de autenticación solo si no estamos en la página de login
        if (error.response?.status === 401 && window.location.pathname !== '/login') {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          // Solo redirigir si no estamos ya en la página de login
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Realizar petición GET
   */
  async get<T = any>(url: string, params?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get<ApiResponse<T>>(url, { params, ...config });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Realizar petición GET para obtener archivos (Blob)
   */
  async getBlob(url: string, params?: any, config?: AxiosRequestConfig): Promise<Blob> {
    try {
      const response = await this.client.get(url, {
        params,
        responseType: 'blob',
        // Nota: los PDFs/ZIPs (Playwright) pueden tardar más de 10s en generarse.
        // Subimos el timeout SOLO para descargas.
        timeout: 300000,
        ...config,
      });
      return response.data;
    } catch (error: any) {
      const responseData = error?.response?.data;
      if (responseData instanceof Blob) {
        let parsed: any = null;
        try {
          const raw = await responseData.text();
          parsed = JSON.parse(raw);
        } catch {
          // Mantener el manejo estándar si la respuesta no es JSON.
        }
        if (parsed?.message) throw new Error(parsed.message);
      }
      throw this.handleError(error);
    }
  }

  /**
   * Realizar petición POST
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Realizar petición PUT
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Realizar petición PATCH
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.patch<ApiResponse<T>>(url, data, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Realizar petición DELETE
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete<ApiResponse<T>>(url, config);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Manejar errores de la API
   */
  private handleError(error: any): Error {
    if (error.response) {
      // Error de respuesta del servidor
      const { data, status } = error.response;

      // Asegurarse de que data existe y tiene la estructura esperada
      const errorMessage = (data && typeof data === 'object' && 'message' in data)
        ? data.message
        : `Error ${status || 'desconocido'}`;

      // Preservar el objeto completo del error para acceso a errors[]
      const apiError: any = new Error(errorMessage);
      apiError.response = error.response; // Mantener referencia al response completo

      // Si hay un mensaje en la respuesta del servidor, usarlo
      if (data && typeof data === 'object' && 'message' in data) {
        apiError.message = data.message;
      }

      // Si el response es un Blob (error en descarga de archivo), intentar leerlo
      if (data instanceof Blob && data.type === 'application/json') {
        // No podemos leer el blob síncronamente aquí fácilmente, 
        // pero podríamos retornar un error genérico o intentar parsearlo.
        // Por simplicidad, retornamos el error genérico.
        apiError.message = "Error al descargar el archivo.";
      }

      // Mensajes personalizados por código de estado
      if (status === 401) {
        // 401 = No autenticado o token inválido/expirado
        apiError.message = (data && typeof data === 'object' && 'message' in data)
          ? data.message
          : 'Sesión expirada. Por favor, inicia sesión nuevamente.';
      } else if (status === 403) {
        // 403 = Autenticado pero sin permisos suficientes
        apiError.message = (data && typeof data === 'object' && 'message' in data)
          ? data.message
          : 'Permisos insuficientes';
      } else if (status === 404) {
        apiError.message = 'Recurso no encontrado.';
      } else if (status === 400) {
        // 400 = Bad Request
        apiError.message = (data && typeof data === 'object' && 'message' in data)
          ? data.message
          : 'Solicitud inválida';
      }

      return apiError;
    } else if (error.code === 'ECONNABORTED') {
      const timeoutErr: any = new Error(
        'La operación tardó demasiado y el navegador canceló la petición. Intenta de nuevo y deja la pestaña abierta mientras se restaura.'
      );
      timeoutErr.isTimeout = true;
      return timeoutErr;
    } else if (error.request) {
      // Error de red (ECONNRESET, ECONNREFUSED, ETIMEDOUT): backend no responde o proxy no puede conectar.
      // Suele ocurrir si el backend está reiniciando o no está corriendo.
      const networkErr: any = new Error(
        'El servidor no está disponible. Comprueba que el backend esté en ejecución y reintenta en unos segundos.'
      );
      networkErr.isNetworkError = true;
      return networkErr;
    } else {
      // Error de configuración
      return new Error('Error de configuración de la petición.');
    }
  }
}

// Exportar instancia única del cliente API
export const apiClient = new ApiClient();

/**
 * Funciones específicas para endpoints de años académicos
 */
export const academicYearApi = {
  // Listar años académicos
  list: (filters?: any) => apiClient.get('/years', filters),

  // Obtener año académico por ID
  getById: (id: string, params?: any) => apiClient.get(`/years/${id}`, params),

  // Obtener detalle del año académico (alias de getById)
  getDetail: (id: string) => apiClient.get(`/years/${id}`),

  // Crear año académico
  create: (data: any) => apiClient.post('/years', data),

  // Activar año académico
  activate: (id: string) => apiClient.post(`/years/${id}/activate`),

  // Migrar año académico a un nuevo borrador
  migrate: (id: string) => apiClient.post(`/years/${id}/migrate`),

  // Cerrar año académico
  close: (id: string) => apiClient.post(`/years/${id}/close`),

  // Eliminar año académico
  delete: (id: string) => apiClient.delete(`/years/${id}`),

  // Obtener grados del año
  getGrades: (yearId: string) => apiClient.get(`/years/${yearId}/grades`),

  // Activar/desactivar grado
  toggleGrade: (yearId: string, gradeId: string, active: boolean) =>
    apiClient.patch(`/years/${yearId}/grades/${gradeId}`, { active }),

  // Activar/desactivar sección
  toggleSection: (yearId: string, gradeId: string, sectionId: string, active: boolean) =>
    apiClient.patch(`/years/${yearId}/grades/${gradeId}/sections/${sectionId}`, { active }),

  // Obtener precios del año
  getPricing: (yearId: string) => apiClient.get(`/years/${yearId}/pricing`),

  // Guardar precios
  savePricing: (yearId: string, data: any) => apiClient.put(`/years/${yearId}/pricing`, data),

  // Actualizar política de cobros
  updateBillingPolicy: (yearId: string, data: any) =>
    apiClient.put(`/years/${yearId}/billing-policy`, data),
};

/**
 * Funciones para autenticación
 */
export const authApi = {
  // Login
  login: (email: string, password: string) =>
    apiClient.post('/auth/login', { email, password }),

  // Logout
  logout: () => apiClient.post('/auth/logout'),

  // Obtener perfil del usuario
  getProfile: () => apiClient.get('/auth/profile'),

  // Actualizar perfil del usuario
  updateProfile: (data: { firstName: string; lastName: string; email: string; phone?: string }) =>
    apiClient.put('/auth/profile', data),

  // Cambiar contraseña
  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    apiClient.put('/auth/change-password', data),

  // Refresh token
  refreshToken: () => apiClient.post('/auth/refresh'),
};

/**
 * Funciones para health check
 */
export const healthApi = {
  // Health check
  check: () => apiClient.get('/health'),

  // Información de la API
  info: () => apiClient.get('/info'),
};

/**
 * Funciones específicas para endpoints de estudiantes
 */
export const studentApi = {
  // Obtener lista de estudiantes con filtros y paginación
  list: (filters?: StudentFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    return apiClient.get<StudentsListResponse>(`/students?${params.toString()}`);
  },

  // Exportar estudiantes al formato de hoja de cálculo del backend
  export: (filters?: StudentFilters & { format?: 'xlsx' | 'csv' }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    return apiClient.getBlob(`/students/export?${params.toString()}`);
  },

  // Crear nuevo estudiante
  create: (data: CreateStudentForm) => apiClient.post<Student>('/students', data),

  // Obtener detalle de un estudiante
  getDetail: (studentId: string) => apiClient.get<StudentDetail>(`/students/${studentId}`),

  // Obtener estudiante por ID (alias de getDetail)
  getById: (studentId: string) => apiClient.get<StudentDetail>(`/students/${studentId}`),

  // Actualizar perfil del estudiante
  updateProfile: (studentId: string, data: Partial<StudentProfile>) =>
    apiClient.put<StudentDetail>(`/students/${studentId}/profile`, data),

  // Inscribir estudiante
  enroll: (studentId: string, data: EnrollStudentForm) =>
    apiClient.post<any>(`/students/${studentId}/enroll`, data),

  // Promover estudiante
  promote: (studentId: string, data: PromoteStudentForm) =>
    apiClient.post<any>(`/students/${studentId}/promote`, data),

  // Promoción masiva al siguiente grado
  bulkPromote: (data: {
    schoolYearId: string;
    sourceGradeId: string;
    sourceSectionId: string;
    targetSectionId: string;
    studentIds: string[];
  }) => apiClient.post<any>('/students/bulk-promote', data),

  // Archivo terminal de 6to
  bulkArchive: (data: {
    schoolYearId: string;
    sourceGradeId: string;
    sourceSectionId: string;
    studentIds: string[];
  }) => apiClient.post<any>('/students/bulk-archive', data),

  // Eliminar / archivar estudiante
  delete: (studentId: string) => apiClient.delete(`/students/${studentId}`),

  // Cambiar sección de estudiante
  changeSection: (studentId: string, sectionId: string) =>
    apiClient.patch<StudentDetail>(`/students/${studentId}/section`, { sectionId }),

  // Agregar persona autorizada
  addAuthorizedPickup: (studentId: string, data: AuthorizedPickupForm) =>
    apiClient.post<AuthorizedPickupApiResponse>(`/students/${studentId}/pickups`, data),

  // Eliminar persona autorizada
  removeAuthorizedPickup: (studentId: string, pickupId: string) =>
    apiClient.delete(`/students/${studentId}/pickups/${pickupId}`),

  // Marcar documento como recibido
  markDocumentReceived: (studentId: string, data: MarkDocumentReceivedForm) =>
    apiClient.post<DocumentStatusApiResponse>(`/students/${studentId}/documents`, data),

  // Actualizar estado de documento
  updateDocumentStatus: (studentId: string, documentId: string, data: UpdateDocumentStatusForm) =>
    apiClient.patch<DocumentStatusApiResponse>(`/students/${studentId}/documents/${documentId}/status`, data),

  // Obtener estadísticas de estudiantes
  getStats: (params?: { yearId?: string }) => apiClient.get<StudentStats>('/students/stats', params),

  // Importación Masiva
  downloadTemplate: (schoolYearId: string) =>
    apiClient.getBlob('/students/import/template', { schoolYearId }),

  previewImport: (formData: FormData) =>
    apiClient.post('/students/import/preview', formData),

  commitImport: (data: { rows: any[]; context: any }) =>
    apiClient.post('/students/import/commit', data),

  previewRosterSync: (formData: FormData) => apiClient.post('/students/import/preview', formData),

  commitRosterSync: (data: { rows: any[]; context: any; preview: any }) =>
    apiClient.post('/students/import/commit', data),
  // Eliminar masivamente estudiantes por sección
  deleteBySection: (sectionId: string, schoolYearId: string, gradeId: string) =>
    apiClient.delete(`/students/section/${sectionId}`),
};


/**
 * Funciones específicas para endpoints de docentes
 */
export const teacherApi = {
  // Listar docentes
  list: (filters?: TeacherFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    return apiClient.get<Teacher[]>(`/teachers?${params.toString()}`);
  },

  // Crear docente
  create: (data: CreateTeacherForm) => apiClient.post<Teacher>('/teachers', data),

  // Obtener docente por ID
  getById: (teacherId: string) => apiClient.get<Teacher>(`/teachers/${teacherId}`),

  // Actualizar docente
  update: (teacherId: string, data: UpdateTeacherForm) =>
    apiClient.put<Teacher>(`/teachers/${teacherId}`, data),

  // Obtener asignaciones del docente autenticado (para vista "Mis Clases")
  getMyAssignments: (schoolYearId?: string) => {
    const params = schoolYearId ? `?schoolYearId=${schoolYearId}` : '';
    return apiClient.get<TeachingAssignment[]>(`/teachers/my-assignments${params}`);
  },

  // Obtener asignaciones de un docente
  getAssignments: (teacherId: string, schoolYearId?: string) => {
    const params = schoolYearId ? `?schoolYearId=${schoolYearId}` : '';
    return apiClient.get<TeachingAssignment[]>(`/teachers/${teacherId}/assignments${params}`);
  },

  // Crear asignación docente (una)
  createAssignment: (data: CreateTeachingAssignmentForm) =>
    apiClient.post<{ success: boolean; data: TeachingAssignment; message: string }>('/teachers/assignments', data),

  // Crear varias asignaciones (mismo docente, misma asignatura, múltiples secciones)
  // T = payload interno (response.data en ApiResponse<T>)
  createAssignmentsBulk: (data: CreateTeachingAssignmentsBulkForm) =>
    apiClient.post<{
      created: TeachingAssignment[];
      skipped: Array<{ sectionId: string; reason: string }>;
    }>('/teachers/assignments/bulk', data),

  // Listar asignaciones
  listAssignments: (filters?: {
    assignmentId?: string;
    schoolYearId?: string;
    teacherId?: string;
    sectionId?: string;
    subjectId?: string;
    isActive?: boolean;
    page?: number;
    pageSize?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    return apiClient.get<TeachingAssignment[]>(`/teachers/assignments?${params.toString()}`);
  },

  // Actualizar asignación
  updateAssignment: (assignmentId: string, data: { isActive?: boolean }) =>
    apiClient.put<{ success: boolean; data: TeachingAssignment; message: string }>(
      `/teachers/assignments/${assignmentId}`,
      data
    ),

  // Eliminar asignación docente (destructivo: elimina calificaciones asociadas)
  deleteAssignment: (assignmentId: string) =>
    apiClient.delete<{ success: boolean; data: any; message: string }>(`/teachers/assignments/${assignmentId}`),

  // Importación masiva
  downloadImportTemplate: () => apiClient.getBlob('/teachers/import/template'),
  previewImport: (formData: FormData) => apiClient.post<{ rows: any[]; summary: any }>('/teachers/import/preview', formData),
  commitImport: (data: { rows: any[] }) => apiClient.post<{ createdCount: number; failedCount: number; failedRows: any[] }>('/teachers/import/commit', data),
};

/**
 * Funciones para gestión de Personal (usuarios del sistema). Solo ADMIN.
 */
export const userApi = {
  list: (filters?: StaffListFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }
    return apiClient.get<User[]>(`/users?${params.toString()}`);
  },
  getById: (id: string) => apiClient.get<User>(`/users/${id}`),
  create: (data: CreateStaffUserForm) => apiClient.post<User>('/users', data),
  update: (id: string, data: UpdateStaffUserForm) => apiClient.put<User>(`/users/${id}`, data),
  delete: (id: string) => apiClient.delete(`/users/${id}`),
};

/**
 * Funciones específicas para endpoints de asignaturas
 */
export const subjectApi = {
  // Listar asignaturas (catálogo)
  list: (filters?: {
    search?: string;
    page?: number;
    pageSize?: number;
    schoolYearId?: string;
    gradeLevel?: string;
    type?: 'ACADEMIC' | 'TECH_MODULE';
    baseLocked?: boolean;
    isActive?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/subjects?${params.toString()}`);
  },

  // Obtener asignatura por ID
  getById: (subjectId: string) => apiClient.get(`/subjects/${subjectId}`),
  getYearProfile: (subjectId: string, schoolYearId: string) => apiClient.get(`/subjects/${subjectId}/years/${schoolYearId}`),

  // Crear asignatura
  create: (data: any) => apiClient.post('/subjects', data),

  // Actualizar asignatura
  update: (subjectId: string, data: any) => apiClient.put(`/subjects/${subjectId}`, data),
  updateYearProfile: (subjectId: string, schoolYearId: string, data: any) => apiClient.put(`/subjects/${subjectId}/years/${schoolYearId}`, data),

  // Configurar RA de módulo técnico (TECH_MODULE)
  updateTechnicalModuleConfig: (subjectId: string, data: { schoolYearId: string; raCount: number; raConfig: Array<{ raIndex: number; raName: string; raValue: number }> }) =>
    apiClient.put(`/subjects/${subjectId}/technical-module/config`, data),

  // Listar curriculum subjects (oferta académica)
  listCurriculum: (filters?: {
    schoolYearId?: string;
    gradeLevel?: string;
    bachillerato?: string | null;
    subjectId?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (key === 'bachillerato' && value === null) {
          // `null` representa la oferta académica común y debe viajar
          // explícitamente; omitirlo haría que el backend devolviera todos
          // los bachilleratos.
          params.append(key, 'null');
        } else if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }
    return apiClient.get(`/subjects/curriculum?${params.toString()}`);
  },

  // Obtener Resultados de Aprendizaje (RAs)
  getLearningResults: (subjectId: string) => apiClient.get(`/subjects/${subjectId}/learning-results`),

  // Generar asignaturas base para un año escolar (seed)
  seedSubjects: (schoolYearId: string) => apiClient.post(`/subjects/seed/${schoolYearId}`),

  // Eliminar asignaturas base de un año escolar (para regenerar)
  deleteBaseSubjects: (schoolYearId: string) => apiClient.delete(`/subjects/seed/${schoolYearId}`),
};

/**
 * Funciones específicas para endpoints de calificaciones
 */
export const gradeApi = {
  // Obtener estudiantes de una asignación
  getAssignmentStudents: (assignmentId: string) =>
    apiClient.get(`/grades/entries/students?assignmentId=${assignmentId}`),

  // Obtener calificaciones de una asignación
  getAssignmentGrades: (assignmentId: string, periodType: string) =>
    apiClient.get(`/grades/entries?assignmentId=${assignmentId}&periodType=${periodType}`),

  // Crear o actualizar nota individual
  createOrUpdateGrade: (data: any) => apiClient.post('/grades/entries', data),

  // Carga masiva de notas
  bulkUpdateGrades: (data: any) => apiClient.post('/grades/entries/bulk', data),

  // Registrar nota técnica
  registerTechnicalGrade: (data: RegisterTechnicalGradeInput) => apiClient.post('/grades/entries/technical', data),

  // Obtener calificaciones técnicas
  getTechnicalGrades: (assignmentId: string) => apiClient.get(`/grades/entries/technical?assignmentId=${assignmentId}`),

  // Obtener datos para calificar módulos técnicos (TECH_MODULE) por RA
  getTechModuleGrades: (assignmentId: string) => apiClient.get(`/grades/tech-modules?assignmentId=${assignmentId}`),

  // Obtener situación final académica por asignación (PC/CF/Completiva/Extra/Special/A-R)
  getAcademicFinalSituation: (assignmentId: string) =>
    apiClient.get(`/grades/assignments/academic-final-situation?assignmentId=${assignmentId}`),

  // Guardar recuperación académica (CEC / CE.EX / CE) por asignación
  bulkUpsertAcademicRecovery: (data: {
    assignmentId: string;
    updates: Array<{
      enrollmentId: string;
      completivaScore?: number | null;
      extraordinariaScore?: number | null;
      especialScore?: number | null;
    }>;
  }) => apiClient.post('/grades/assignments/academic-recovery/bulk', data),

  // Guardado masivo de módulos técnicos (TECH_MODULE) por RA
  bulkUpsertTechModuleGrades: (data: {
    assignmentId: string;
    updates: Array<{
      enrollmentId: string;
      raIndex: number;
      cra: number | null;
      rp1: number | null;
      rp2: number | null;
    }>;
    specialEvaluations?: Array<{
      enrollmentId: string;
      specialEvaluationScore: number | null;
    }>;
  }) => apiClient.post('/grades/tech-modules/bulk', data),

  /** Actualizar config RA del gradebook (docente redistribuye valores; misma cantidad de RAs, suma 100). */
  updateGradebookRAConfig: (assignmentId: string, data: { raConfig: Array<{ raIndex: number; raName?: string | null; raValue: number }> }) =>
    apiClient.put(`/grades/assignments/${assignmentId}/tech-module-ra-config`, data),

  // Listar competencias
  listCompetencies: (isActive?: boolean) => {
    const params = isActive !== undefined ? `?isActive=${isActive}` : '';
    return apiClient.get(`/grades/competencies${params}`);
  },

  // Obtener estudiantes de una sección (para boletín completo)
  getSectionStudents: (sectionId: string, schoolYearId: string) =>
    apiClient.get(`/grades/sections/students?sectionId=${sectionId}&schoolYearId=${schoolYearId}`),

  // Obtener todas las asignaciones de una sección
  getSectionAssignments: (sectionId: string, schoolYearId: string) =>
    apiClient.get(`/grades/sections/assignments?sectionId=${sectionId}&schoolYearId=${schoolYearId}`),

  // Obtener todas las calificaciones de una sección
  getSectionGrades: (sectionId: string, schoolYearId: string) =>
    apiClient.get(`/grades/sections/grades?sectionId=${sectionId}&schoolYearId=${schoolYearId}`),

  // Obtener estado de promoción por sección (cards + detalle)
  getSectionPromotionStatus: (sectionId: string, schoolYearId: string) =>
    apiClient.get(`/grades/sections/promotion-status?sectionId=${sectionId}&schoolYearId=${schoolYearId}`),

  getPromotionReport: (params: { schoolYearId: string; scope: 'INSTITUTION' | 'GRADE'; gradeId?: string }) =>
    apiClient.get('/grades/promotion-report', params),

  // Obtener ranking de excelencia académica académico-técnico
  getStudentRanking: (params: {
    schoolYearId: string;
    scope: StudentRankingScope;
    sectionId?: string;
    gradeId?: string;
    rankingMode?: StudentRankingMode;
  }) => apiClient.get<StudentRankingResponse>('/grades/student-ranking', params),
};

/**
 * Funciones para endpoints de boletines/reportes
 */
export const anecdotalApi = {
  createRecord: (data: {
    studentId: string;
    assignmentId: string;
    description: string;
    recordDate: string;
    recordTime: string | null;
    recordType: 'POSITIVO' | 'ADVERTENCIA' | 'FALTA';
  }) => apiClient.post('/anecdotal-records', data),

  getRecords: (filters?: {
    studentId?: string;
    assignmentId?: string;
    gradeId?: string;
    sectionId?: string;
    schoolYearId?: string;
    startDate?: string;
    endDate?: string;
    recordType?: 'POSITIVO' | 'ADVERTENCIA' | 'FALTA';
  }) => {
    const params = new URLSearchParams();
    if (filters?.studentId) params.append('studentId', filters.studentId);
    if (filters?.assignmentId) params.append('assignmentId', filters.assignmentId);
    if (filters?.gradeId) params.append('gradeId', filters.gradeId);
    if (filters?.sectionId) params.append('sectionId', filters.sectionId);
    if (filters?.schoolYearId) params.append('schoolYearId', filters.schoolYearId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.recordType) params.append('recordType', filters.recordType);
    return apiClient.get(`/anecdotal-records?${params.toString()}`);
  },

  exportRecords: (filters?: {
    studentId?: string;
    assignmentId?: string;
    gradeId?: string;
    sectionId?: string;
    schoolYearId?: string;
    startDate?: string;
    endDate?: string;
    recordType?: 'POSITIVO' | 'ADVERTENCIA' | 'FALTA';
  }) => {
    const params = new URLSearchParams();
    if (filters?.studentId) params.append('studentId', filters.studentId);
    if (filters?.assignmentId) params.append('assignmentId', filters.assignmentId);
    if (filters?.gradeId) params.append('gradeId', filters.gradeId);
    if (filters?.sectionId) params.append('sectionId', filters.sectionId);
    if (filters?.schoolYearId) params.append('schoolYearId', filters.schoolYearId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.recordType) params.append('recordType', filters.recordType);
    return apiClient.getBlob(`/anecdotal-records/export?${params.toString()}`);
  },

  updateRecord: (recordId: string, data: {
    description?: string;
    recordDate?: string;
    recordTime?: string | null;
    recordType?: 'POSITIVO' | 'ADVERTENCIA' | 'FALTA';
  }) => apiClient.put(`/anecdotal-records/${recordId}`, data),

  deleteRecord: (recordId: string) => apiClient.delete(`/anecdotal-records/${recordId}`),
};

export const institutionalActivityApi = {
  list: (filters: { schoolYearId: string; from?: string; to?: string; search?: string }) =>
    apiClient.get<InstitutionalActivity[]>('/institutional-activities', filters),
  create: (data: InstitutionalActivityInput) =>
    apiClient.post<InstitutionalActivity>('/institutional-activities', data),
  update: (id: string, data: Partial<Omit<InstitutionalActivityInput, 'schoolYearId'>>) =>
    apiClient.put<InstitutionalActivity>(`/institutional-activities/${id}`, data),
  delete: (id: string) => apiClient.delete(`/institutional-activities/${id}`),
};

/**
 * Funciones para endpoints de boletín oficial (PDF individual y bulk)
 */
export const bulletinApi = {
  // Obtener DTO del boletín oficial
  getOfficialBulletin: (enrollmentId: string) =>
    apiClient.get(`/bulletins/student/${enrollmentId}`),

  // Generar y descargar PDF del boletín
  generatePDF: (enrollmentId: string, format: 'a4' | 'oficio' = 'a4') =>
    apiClient.getBlob(`/bulletins/student/${enrollmentId}/pdf?format=${format}`),

  // Generar PDFs bulk por sección (ZIP)
  generatePDFsBulk: (sectionId: string, schoolYearId: string, format: 'a4' | 'a4-vertical' | 'a4-horizontal' | 'oficio' = 'a4') =>
    apiClient.getBlob(`/bulletins/section/${sectionId}/pdf-bulk?schoolYearId=${schoolYearId}&format=${format}`),

  // Generar un único PDF con todos los boletines de la sección concatenados
  generatePDFsCombined: (sectionId: string, schoolYearId: string, format: 'a4' | 'a4-vertical' | 'a4-horizontal' | 'oficio' = 'a4') =>
    apiClient.getBlob(`/bulletins/section/${sectionId}/pdf-combined?schoolYearId=${schoolYearId}&format=${format}`),
};
